import React, { useMemo } from 'react';

import { numberToCurrencyString } from '../../../../services/formatting';
import Button from '../../../Forms/Button';
import { numberToCurrencyTemplate } from '../../../../services/formatting.templates';
import { StripePricingPlan } from '../../../../models/Stripe';
import AccountConsumptionChart from './chart/AccountConsumptionChart';
import ConfirmCancelPlanModal from './CancelPlanConfirm/CancelPlanConfirm';
import { graphqlSchema } from '../../../../services/graphql.schema';
import { useMutation } from '@apollo/react-hooks';
import sharedToasterSubject from '../../../../services/shared.toasterSubject';
import GraphQlClient from '../../../../services/graphql';
import { BillingAccount } from '../../../../models/BillingAccount';
import './AccountInfo.scss';

interface AccountInfoProps {
    pricingPlans: StripePricingPlan[];
    isClusterAccount: boolean;
    billingAccount: BillingAccount | null;
    onAddBalanceClick: () => void;
}

function AccountInfo({
    pricingPlans,
    isClusterAccount,
    billingAccount,
    onAddBalanceClick
}: AccountInfoProps) {
    const [showCancelModal, setShowCancelModal] = React.useState(false);

    const [requestCancelSubscription] = useMutation(graphqlSchema.STRIPE.PRICES.cancelSubscription, {
        onCompleted: ({ cancelSubscription }: { cancelSubscription: boolean }) => {
            GraphQlClient.recallCurrentUser();
            if (cancelSubscription) {
                sharedToasterSubject.next({ type: 'warning', message: 'Suscripción cancelada.' });
            } else {
                sharedToasterSubject.next({ type: 'error', message: 'No se pudo cancelar la suscripción. Por favor intenta de nuevo o contacta al soporte.' });
            }
        },
        onError: (err) => {
            console.error('Error cancelling subscription:', err.message);
            sharedToasterSubject.next({ type: 'error', message: err.message || 'Error al cancelar la suscripción. Por favor intenta de nuevo o contacta al soporte.' });
        }
    })

    const currentPlan = useMemo(() => {
        if(!billingAccount) return null;
        if(!pricingPlans?.length) return null;
        if(!billingAccount.hasStripeSubscription) return null;
        if(!billingAccount.stripeSubscriptionCfdiUsage) return null;

        const plan = pricingPlans.find((plan) => plan.id === billingAccount.stripeSubscriptionCfdiUsage.priceId);
        return plan || null;
    }, [billingAccount, pricingPlans]);

    if (isClusterAccount) {
        return (
            <div className="balanceSection card">
                <div className="row">
                    <h4 className="center">Saldo administrado por <b>{billingAccount?.clusterName}</b></h4>
                </div>
            </div>
        )
    }

    const handleCancelSubsButton = () => {
        setShowCancelModal(true);
    }

    const onConfirmCancelSubs = () => {
        setShowCancelModal(false);
        requestCancelSubscription()
    }

    // Standard PrePay Account View
    if (!billingAccount?.hasStripeSubscription) {
        return (
            <div className="balanceSection">
                <div className='row'>
                    <p className='accountInfo'>Tu plan de pago actual es de <b>Recarga</b></p>
                </div>
                <div className='row'>
                    <div className="balance">
                        <h4>Saldo actual: </h4>
                        <h4 className="currency">$ {numberToCurrencyTemplate(!!billingAccount ? billingAccount.balance : 0)}</h4>
                    </div>
                    <p className='costBillInfo'>Costo por factura a ${numberToCurrencyString(2.95)}MXN</p>
                </div>
                <Button
                    primary={true}
                    size='small'
                    handleClick={onAddBalanceClick}
                    disabled={!!isClusterAccount}
                >
                    <span>Agregar saldo</span>
                </Button>
            </div>
        )
    }

    if (currentPlan && billingAccount?.hasStripeSubscription) {
        const usageText = billingAccount.stripeSubscriptionCfdiUsage.isMeteredBilling ? `${billingAccount.stripeSubscriptionCfdiUsage.currentUsage}` : `${billingAccount.stripeSubscriptionCfdiUsage.currentUsage} / ${billingAccount.stripeSubscriptionCfdiUsage.maxUsage}`;
        const periodStartText = billingAccount.stripeSubscriptionCfdiUsage.periodStartsAt ? new Date(billingAccount.stripeSubscriptionCfdiUsage.periodStartsAt).toLocaleDateString('es-MX', { month: 'long', day: 'numeric'}) : '';
        const periodEndText = billingAccount.stripeSubscriptionCfdiUsage.periodEndsAt ? new Date(billingAccount.stripeSubscriptionCfdiUsage.periodEndsAt).toLocaleDateString('es-MX', { month: 'long', day: 'numeric'}) : '';
        return (
            <div className="planSection">
                <div className='planInfo row'>
                    <div className='planNameWrapper'>
                        <h4  className='planName'>{currentPlan.name}</h4>
                        <button className='cancelSubs' onClick={handleCancelSubsButton}>
                            Cancelar plan
                        </button>
                    </div>
                    <p className='planUsage'> 
                        {usageText} facturas
                    </p>
                </div>

                <div className='planConsumption row'>
                    <div className='consumtionPeriod'>
                        <h4>Historial de consumo facturación</h4>
                        <p>Periodo: <b>{periodStartText}</b> a <b>{periodEndText}</b></p>
                    </div>
                    <AccountConsumptionChart
                        periodStartsAt={billingAccount.stripeSubscriptionCfdiUsage.periodStartsAt}
                        periodEndsAt={billingAccount.stripeSubscriptionCfdiUsage.periodEndsAt} // Add one day to include the end date in the chart
                    />
                    
                </div>
                <ConfirmCancelPlanModal 
                    isOpen={showCancelModal}
                    onRequestClose={() => setShowCancelModal(false)}
                    onConfirm={onConfirmCancelSubs}
                />
            </div>
        )
    }

    // Default returns loading state
    return (
        <div></div>
    )
}

export default AccountInfo;